import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { errorMsg, userListToLabelEmailPhoneValue } from "../../../Apis/functions";
import { PersonChipBlack } from "../../../components/buttons/IconButtons";
import { GetAllVisitorTypes } from "../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {
    CreateInvitation, GetAllVisitors, PreRegisterVisitor
} from "../../../reduxToolkit/Invitation/InvitationApi";
import { GetTolerances } from "../../../reduxToolkit/SystemConfig/SystemConfigApi";
import {
    getVisitorTypeCardByVisitor, getVisitorTypeIdByKey, MY_APARTMENT_INVITATION_MODEL,
    MY_APARTMENT_INVITATION_STEPS, MY_APARTMENT_VISITOR_OPTION_MODEL
} from "../Model/Model";
import { INVITABLE_VISITOR_TYPES } from "../../Dashboard/Visitor/Type/Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Invite external user modal logic: the step the resident is standing on, the
 * visitor types offered, the visitor picked on the search of the ones already
 * registered, the invitation form and the handlers the modal binds to the cards,
 * to the inputs and to the footer buttons.
 *
 * @param {Object} props
 * @param {boolean} props.show - Modal visibility, the wizard is reset every time it changes.
 * @param {Function} props.onHide - Closes the modal.
 * @param {Object} [props.buildingDetail] - Building of the logged resident, the invitation is created on it.
 * @param {Function} [props.setModified] - Raises the flag that reloads the invitations of the tab.
 * @returns {Object} Localization function, wizard state, form state, visitor search state and the modal handlers.
 */
export const InviteExternalUserModalApp = ({ show, onHide = () => {}, buildingDetail, setModified } = {}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // Logged resident, they have no employee record behind so the user of the
    // session is the host of the invitation
    const hostId = sessionStorage.getItem('id');
    // Visitor types of the catalog, the card picked is matched against them
    const visitorTypesCatalog = useSelector((state) => state?.EmployeeEventsSlice?.getAllVisitorTypes);
    // Tolerance of the system config, the resident is never asked for it
    const [accessToleranceMin, setAccessToleranceMin] = useState(null);
    const [activeStep, setActiveStep] = useState(MY_APARTMENT_INVITATION_STEPS.VISITOR_TYPE);
    const [emptyFields, setEmptyFields] = useState(false);
    const [visitorType, setVisitorType] = useState(null);
    const [invitation, setInvitation] = useState({ ...MY_APARTMENT_INVITATION_MODEL });
    // Visitors already registered, the whole list is read once the modal opens and
    // the chip of the first step filters it without going back to the service
    const [visitors, setVisitors] = useState([]);
    // Visitor picked on the first step, it travels as the single chip of
    // CustomAddChip and fills the contact of the second one
    const [selectedVisitors, setSelectedVisitors] = useState([]);
    // The visitor picked brings their own type, the cards stay locked on it until
    // that visitor is removed or changed
    const [isVisitorTypeLocked, setIsVisitorTypeLocked] = useState(false);

    const visitorTypes = INVITABLE_VISITOR_TYPES;

    // The contact of the second step is already known when the visitor picked is
    // one of the registered ones, so that step is not walked
    const steps = useMemo(() => (
        selectedVisitors.length > 0
            ? [MY_APARTMENT_INVITATION_STEPS.VISITOR_TYPE, MY_APARTMENT_INVITATION_STEPS.SCHEDULE]
            : Object.values(MY_APARTMENT_INVITATION_STEPS)
    ), [selectedVisitors]);

    const stepIndex = Math.max(steps.indexOf(activeStep), 0);

    const isLastStep = activeStep === MY_APARTMENT_INVITATION_STEPS.SCHEDULE;

    // Filled bar of the modal header, one part of it per step already reached
    const progress = ((stepIndex + 1) * 100) / steps.length;

    useEffect(() => {
        reset();

        if (!show) return;

        getVisitors();
        dispatch(GetAllVisitorTypes());
        getTolerance();
    }, [show]);

    /**
     * Reads the access tolerance of the system config, the invitation created
     * here travels with the default one because the wizard does not ask for it.
     */
    const getTolerance = () => {
        dispatch(GetTolerances()).then(({ payload: { data: { data, success } } }) => {
            if (success !== true) return;

            setAccessToleranceMin(data?.accessToleranceJustOnceMin ?? null);
        });
    };

    /**
     * Reads the visitors already registered and leaves them as the options of the
     * chip of the first step, the visitor of the invitation-service list is
     * identified by its user and carries its name and its email joined, so the
     * option is resolved from them.
     */
    const getVisitors = () => {
        dispatch(GetAllVisitors()).then(({ payload: { data: { data, success } } }) => {
            if (success !== true) return;

            setVisitors(userListToLabelEmailPhoneValue(
                MY_APARTMENT_VISITOR_OPTION_MODEL.setList(data), 'id', true, PersonChipBlack
            ));
        });
    };

    /**
     * Leaves the wizard as it is opened, standing on the first step with no
     * visitor searched, no visitor type picked and every field empty.
     */
    const reset = () => {
        setActiveStep(MY_APARTMENT_INVITATION_STEPS.VISITOR_TYPE);
        setEmptyFields(false);
        setVisitorType(null);
        setInvitation({ ...MY_APARTMENT_INVITATION_MODEL });
        setSelectedVisitors([]);
        setIsVisitorTypeLocked(false);
    };

    /**
     * Keeps the visitor type the resident picked on the cards, clicking the one
     * already selected leaves it selected because the type is required. While the
     * type is the one of the visitor picked the cards do not answer, it is only
     * released by removing or changing that visitor.
     *
     * @param {Object} type - Visitor type of the card clicked.
     */
    const handleSelectVisitorType = (type) => {
        if (isVisitorTypeLocked) return;

        setVisitorType(type);
    };

    /**
     * Fills the contact of the invitation with the visitor picked on the chip,
     * removing the chip empties it again so it can be typed by hand. The visitor
     * already registered was created with a visitor type, so that type is the one
     * of the invitation and the cards are locked on it.
     *
     * @param {Array<Object>} list - Chips picked, only one visitor is allowed.
     */
    const handleSelectVisitors = (list) => {
        const visitor = list?.[list.length - 1] ?? null;
        const visitorTypeCard = getVisitorTypeCardByVisitor(visitorTypesCatalog, visitor);

        setSelectedVisitors(visitor ? [visitor] : []);
        setInvitation((prev) => ({
            ...prev,
            name: visitor?.name ?? '',
            lastName: visitor?.lastName ?? '',
            email: visitor?.email ?? ''
        }));

        if (visitorTypeCard) {
            setVisitorType(visitorTypeCard);
            setIsVisitorTypeLocked(true);
            return;
        }

        // The visitor was removed or changed for one with no type of its own, the
        // cards are picked again by the resident
        if (isVisitorTypeLocked) {
            setVisitorType(null);
            setIsVisitorTypeLocked(false);
        }
    };

    /**
     * Updates the field the resident is typing on, typing over the contact
     * releases the visitor picked on the search because it no longer is the one
     * of the form.
     *
     * @param {Object} e - Input change event.
     */
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (selectedVisitors.length > 0 && ['name', 'lastName', 'email'].includes(name)) {
            setSelectedVisitors([]);
            setIsVisitorTypeLocked(false);
        }

        setInvitation((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * Closes the modal and leaves the wizard on its first step with the form
     * empty.
     */
    const handleClose = () => {
        reset();
        onHide();
    };

    /**
     * Goes back to the previous step of the ones being walked keeping what the
     * resident already filled, on the first step it closes the modal because
     * there is nothing to go back to.
     */
    const handleBack = () => {
        if (activeStep === MY_APARTMENT_INVITATION_STEPS.VISITOR_TYPE) {
            handleClose();
            return;
        }

        setEmptyFields(false);
        setActiveStep(steps[stepIndex - 1]);
    };

    /**
     * Validates the step the resident is standing on before moving to the next
     * one being walked, the last step registers the invitation instead.
     */
    const handleContinue = () => {
        if (activeStep === MY_APARTMENT_INVITATION_STEPS.VISITOR_TYPE) {
            if (!visitorType) {
                return;
            }

            // The contact step is skipped when the visitor picked already brings it
            setActiveStep(steps[stepIndex + 1]);
            return;
        }

        if (activeStep === MY_APARTMENT_INVITATION_STEPS.VISITOR_DATA) {
            if (!invitation?.isVisitorDataValid()) {
                setEmptyFields(true);
                return;
            }

            setEmptyFields(false);
            setActiveStep(MY_APARTMENT_INVITATION_STEPS.SCHEDULE);
            return;
        }

        handleSave();
    };

    /**
     * Pre registers the visitor captured by hand, the invitation is created over
     * the user this returns. The email of the second step is not sent, the
     * service generates the one the visitor is created with.
     *
     * @returns {Promise<string|null>} User of the visitor, null when the pre register failed.
     */
    const preRegisterVisitor = async () => {
        const { payload: { data: { data, success, code } } } =
            await dispatch(PreRegisterVisitor(invitation.toPreRegister()));

        if (success !== true) {
            errorMsg(code);
            return null;
        }

        return data?.id ?? null;
    };

    /**
     * Validates the schedule and creates the invitation. The visitor picked on
     * the search of the first step is already registered, the one captured by
     * hand is pre registered first so the invitation always travels with a user.
     */
    const handleSave = async () => {
        if (!invitation?.isScheduleValid()) {
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);

        const visitorId = selectedVisitors[0]?.id ?? await preRegisterVisitor();

        if (!visitorId) return;

        const body = invitation.toRegister({
            hostId,
            visitorId,
            zoneId: buildingDetail?.zoneId,
            visitorTypeId: getVisitorTypeIdByKey(visitorTypesCatalog, visitorType?.key),
            accessToleranceMin
        });

        dispatch(CreateInvitation(body)).then(({ payload: { data: { success, code } } }) => {
            if (success !== true) {
                errorMsg(code);
                return;
            }

            toast.success(t('has_been_sent_successfully'));
            // Reloads the invitations of the tab with the one just created
            setModified?.(true);
            handleClose();
        });
    };

    return {
        t,
        activeStep,
        isLastStep,
        progress,
        emptyFields,
        visitorType,
        visitorTypes,
        isVisitorTypeLocked,
        invitation,
        visitors,
        selectedVisitors,
        handleSelectVisitors,
        handleSelectVisitorType,
        handleChange,
        handleClose,
        handleBack,
        handleContinue
    };
};
